

import { useEffect, useState } from 'react';
import {
    Stack, TextInput, Textarea, Select, MultiSelect, Checkbox, NumberInput,
    Group, Text, ActionIcon, Divider, Button, Box, Alert
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import {
    toVariableName,
    toEditableVariableKey,
    shouldMirrorLabelToKey,
    findReusablePageVariable,
} from '@/features/editor/utils/variableKey';
import { PageElement, ANSWER_CAPTURING_ELEMENT_TYPES } from '@holloway/dsl';
import { Node } from 'reactflow';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { variablesApi, Variable } from '@/services/variables.api';
import {
    nextOptionLabel,
    withOptionAdded,
    withOptionRemoved,
    withOptionRenamed,
} from '@/features/editor/utils/multiChoiceOptions';
import { isEmptyRichText } from '@/features/editor/utils/renderRichText';
import ScriptEditor from './ScriptEditor';
import { collectHtml } from './ScriptEditor/flowColors';

interface Props {
    element: PageElement;
    onChange: (updates: Partial<PageElement>) => void;
    onRemove?: () => void;
    nodes?: Node[];
    onAddNode?: (type: string) => string;
    variables?: { id: string; label: string; scope?: string; type?: string }[];
    rawVariables?: Variable[];
    flowId?: string;
    pageNodeId?: string;
    pageElements?: PageElement[];
}


/**
 * Surface why a variable could not be saved.
 *
 * The API rejects a duplicate name with 409 and an actionable message. Without
 * this the mutation failed silently, the field snapped back to whatever was
 * stored, and the rename looked like it had simply done nothing.
 */
function notifyVariableError(error: unknown, fallback: string) {
    const message =
        (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
    notifications.show({
        color: 'red',
        title: 'Variable not saved',
        message: Array.isArray(message) ? message.join(' ') : message || fallback,
    });
}

export default function PageElementProperties({
    element,
    onChange,
    onRemove,
    nodes,
    onAddNode,
    variables = [],
    rawVariables = [],
    flowId,
    pageNodeId,
    pageElements = [],
}: Props) {
    const queryClient = useQueryClient();

    // A branch option with no page, or whose page was deleted, leaves a viewer
    // who picks it with nowhere to go; say so where the author sets it.
    const pageIds = new Set((nodes || []).filter((n) => n.type === 'page').map((n) => n.id));
    const branchTargetError = (target: string | undefined) => {
        if (!target) return 'Not connected. Viewers who choose this will be stuck.';
        if (nodes && !pageIds.has(target)) return 'That page no longer exists. Choose another.';
        return undefined;
    };

    const INPUT_ELEMENT_TYPES = ['ShortText', 'Multiline', 'Number', 'Date', 'Password'];
    const isInputElement = INPUT_ELEMENT_TYPES.includes(element.type);

    // Only elements that actually capture a respondent's answer need a variable
    // to store it in. Showing the field on a Button or a display-only element
    // invites authors to bind a variable that nothing will ever write to.
    const VALUE_CAPTURING_TYPES = [
        ...INPUT_ELEMENT_TYPES,
        'YesNo',
        'MultiChoice',
        'Rating',
        // ImageUpload belongs here: the element's value is the uploaded asset
        // id, and PageNodeRenderer reads it via element.varId. Without a
        // variable the upload would succeed and then be discarded.
        'ImageUpload',
    ];
    const capturesValue = VALUE_CAPTURING_TYPES.includes(element.type);

    // Whether this element type silently discards answers when unbound.
    // Mirrors ANSWER_CAPTURING_ELEMENT_TYPES from @holloway/dsl (same list,
    // same exclusions: Password and ImageUpload are excluded).
    const WARN_UNBOUND_TYPES: ReadonlySet<string> = new Set(ANSWER_CAPTURING_ELEMENT_TYPES);
    const warnUnbound = WARN_UNBOUND_TYPES.has(element.type) && !element.varId;
    const DEFAULT_BUTTON_TEXTS = new Set(['', 'Click Me']);

    // The variable this element is bound to (by GUID). Its display name lives on
    // the Variable row, so renaming it never breaks references elsewhere.
    const boundVar = element.varId ? rawVariables.find((v) => v.id === element.varId) : undefined;
    const [varKey, setVarKey] = useState(boundVar?.name ?? '');
    // Whether the user has edited the key themselves. Until they do, the key
    // mirrors the label; the previous logic keyed off "is the key empty", which
    // is only true before the first character and so froze the key at one letter.
    const [varKeyTouched, setVarKeyTouched] = useState(false);

    useEffect(() => {
        setVarKey(boundVar?.name ?? '');
    }, [boundVar?.id, boundVar?.name]);

    const getVarType = (elementType: string): 'STRING' | 'NUMBER' | 'BOOLEAN' => {
        if (elementType === 'Number') return 'NUMBER';
        return 'STRING';
    };

    const createVarMutation = useMutation({
        mutationFn: variablesApi.createVariable,
        onSuccess: (created) => {
            queryClient.invalidateQueries({ queryKey: ['variables'] });
            onChange({ varId: created.id });
        },
        onError: (error) => {
            notifyVariableError(error, 'That variable name is already in use on this page.');
            // Put the field back to what is actually stored, so what is shown
            // and what exists cannot drift apart.
            setVarKey(boundVar?.name ?? '');
        },
    });

    const updateVarMutation = useMutation({
        mutationFn: ({ id, name }: { id: string; name: string }) =>
            variablesApi.updateVariable(id, { name }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['variables'] }),
        onError: (error) => {
            notifyVariableError(error, 'That variable name is already in use on this page.');
            setVarKey(boundVar?.name ?? '');
        },
    });

    // Persist the variable key: rename the bound page variable, or create one
    // (scope PAGE, owned by this page + element) and bind the element to its GUID.
    const commitVarKey = (rawValue: string) => {
        const name = toVariableName(rawValue);
        setVarKey(name);
        if (!name) return;
        if (boundVar) {
            if (boundVar.name !== name) updateVarMutation.mutate({ id: boundVar.id, name });
            return;
        }
        if (!flowId || !pageNodeId) return;

        // Bind to a variable of this name that already exists on this page
        // rather than trying to create a second one. Deleting an element does
        // not delete its variable -- rich-text mentions reference variables by
        // GUID and outlive the element that made them -- so re-adding a field
        // with the same label is a normal thing to do, and used to fail on the
        // (flowId, pageNodeId, name) unique index.
        //
        // The existing variable's type is left as it is: other elements and
        // mentions may already read it, so silently retyping it would change
        // their behaviour.
        const reusable = findReusablePageVariable(rawVariables, { name, flowId, pageNodeId });
        if (reusable) {
            onChange({ varId: reusable.id });
            return;
        }

        createVarMutation.mutate({
            name,
            type: getVarType(element.type),
            scope: 'PAGE',
            flowId,
            pageNodeId,
            elementId: element.id,
        });
    };

    const handleLabelChange = (label: string) => {
        onChange({ label });

        // Mirror the label into the key while the user has not taken it over.
        // Persisting here would create the variable from whatever has been
        // typed so far -- which, on the first keystroke, is a single letter.
        if (shouldMirrorLabelToKey({ label, keyTouched: varKeyTouched, hasBoundVar: !!boundVar, isInputElement })) {
            setVarKey(toVariableName(label));
        }
    };

    /** Persist the mirrored key once the label is finished, not per keystroke. */
    const handleLabelBlur = () => {
        if (
            shouldMirrorLabelToKey({
                label: element.label || '',
                keyTouched: varKeyTouched,
                hasBoundVar: !!boundVar,
                isInputElement,
            })
        ) {
            commitVarKey(varKey);
        }
    };

    const getTargetPageButtonText = (targetPageId?: string | null) => {
        const title = nodes?.find((node) => node.id === targetPageId)?.data?.title;
        return title ? `Go to ${title}` : 'Continue';
    };

    const withTargetPageButtonText = (targetPageId: string) => {
        const currentText = ((element as any).buttonText || '').trim();
        return {
            targetPageId,
            ...(DEFAULT_BUTTON_TEXTS.has(currentText) ? { buttonText: getTargetPageButtonText(targetPageId) } : {}),
        };
    };

    return (
        <Stack gap="md">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                {element.type} Settings
            </Text>

            {element.type !== 'RichText' && (
                <TextInput
                    label="Label"
                    description={isInputElement ? 'This is what people see on the page.' : undefined}
                    value={element.label || ''}
                    onChange={(e) => handleLabelChange(e.currentTarget.value)}
                    onBlur={handleLabelBlur}
                />
            )}

            {/* `|| boundVar` so an element that already has a variable — a Button
                in an existing flow, say — keeps it visible and editable rather
                than stranding a binding that still gets written at runtime. */}
            {(capturesValue || boundVar) && (
                <Box>
                    <TextInput
                        label="Variable key"
                        description="Saved as a page variable. Type @ to insert it in messages and rich text."
                        value={varKey}
                        onChange={(e) => {
                            setVarKeyTouched(true);
                            setVarKey(toEditableVariableKey(e.currentTarget.value));
                        }}
                        onBlur={(e) => commitVarKey(e.currentTarget.value)}
                    />
                    {boundVar && pageNodeId && (
                        <Text size="xs" c="dimmed" mt={2}>
                            Available across the flow as a page variable.
                        </Text>
                    )}
                    {warnUnbound && (
                        <Alert
                            mt="xs"
                            color="yellow"
                            variant="light"
                            icon={<IconAlertTriangle size={16} />}
                            title="Answers won't be saved"
                            data-testid="unbound-variable-warning"
                        >
                            Set a variable key to keep them.
                        </Alert>
                    )}
                </Box>
            )}

            {/* RichText Editor */}
            {element.type === 'RichText' && (
                <Box>
                    <Text size="sm" fw={500} mb="xs">Content</Text>
                    <ScriptEditor
                        content={isEmptyRichText((element as any).content) ? '' : (element as any).content}
                        // Rich text elsewhere in the flow, for the color pickers' swatches.
                        // This page is read from its live elements, which hold unsaved
                        // edits; this element is left out because its editor supplies
                        // its own current content.
                        flowContent={[
                            ...collectHtml((nodes || []).filter((node) => node.id !== pageNodeId).map((node) => node.data)),
                            ...collectHtml(pageElements.filter((el) => el.id !== element.id)),
                        ]}
                        onChange={(content) => onChange({ content })}
                        placeholder="Enter your rich text here..."
                        variables={variables}
                    />
                </Box>
            )}

            {/* Common Input Properties */}
            {['ShortText', 'Multiline', 'Date', 'Number', 'Password', 'MultiChoice', 'Rating'].includes(element.type) && (
                <Checkbox
                    label="Required"
                    checked={element.required || false}
                    onChange={(e) => onChange({ required: e.currentTarget.checked })}
                />
            )}

            {/* Image Upload */}
            {element.type === 'ImageUpload' && (
                <>
                    <NumberInput
                        label="Maximum file size (MB)"
                        description="Capped at 25MB by the server, whatever is set here."
                        min={1}
                        max={25}
                        clampBehavior="strict"
                        value={(element as any).maxSizeMB ?? 25}
                        onChange={(value) =>
                            onChange({ maxSizeMB: typeof value === 'number' ? value : 25 } as any)
                        }
                    />
                    <MultiSelect
                        label="Accepted formats"
                        description="Leave all selected to accept any supported image."
                        data={[
                            { value: 'jpg', label: 'JPG' },
                            { value: 'png', label: 'PNG' },
                            { value: 'webp', label: 'WebP' },
                            { value: 'gif', label: 'GIF' },
                        ]}
                        value={(element as any).acceptedFormats ?? ['jpg', 'png', 'webp', 'gif']}
                        onChange={(value) =>
                            // An empty list would reject everything, so fall back
                            // to the full set rather than silently blocking uploads.
                            onChange({
                                acceptedFormats: value.length > 0 ? value : ['jpg', 'png', 'webp', 'gif'],
                            } as any)
                        }
                    />
                    <Checkbox
                        label="Allow multiple images"
                        checked={Boolean((element as any).multiple)}
                        onChange={(e) => onChange({ multiple: e.currentTarget.checked } as any)}
                    />
                </>
            )}

            {/* Specific Properties */}
            {element.type === 'ShortText' && (
                <TextInput
                    label="Placeholder"
                    value={(element as any).placeholder || ''}
                    onChange={(e) => onChange({ placeholder: e.currentTarget.value })}
                />
            )}

            {element.type === 'Multiline' && (
                <>
                    <TextInput
                        label="Placeholder"
                        value={(element as any).placeholder || ''}
                        onChange={(e) => onChange({ placeholder: e.currentTarget.value })}
                    />
                    <NumberInput
                        label="Rows"
                        value={(element as any).rows || 4}
                        onChange={(val) => onChange({ rows: Number(val) })}
                    />
                </>
            )}

            {element.type === 'Number' && (
                <Group grow>
                    <NumberInput
                        label="Min"
                        value={(element as any).min}
                        onChange={(val) => onChange({ min: typeof val === 'number' ? val : undefined })}
                    />
                    <NumberInput
                        label="Max"
                        value={(element as any).max}
                        onChange={(val) => onChange({ max: typeof val === 'number' ? val : undefined })}
                    />
                </Group>
            )}

            {element.type === 'YesNo' && (
                <>
                    <Group grow>
                        <TextInput
                            label="Yes Label"
                            value={(element as any).yesLabel || 'Yes'}
                            onChange={(e) => onChange({ yesLabel: e.currentTarget.value })}
                        />
                        <TextInput
                            label="No Label"
                            value={(element as any).noLabel || 'No'}
                            onChange={(e) => onChange({ noLabel: e.currentTarget.value })}
                        />
                    </Group>

                    <Divider label="Branching" labelPosition="center" my="sm" />

                    <Checkbox
                        label="Create branches to other pages"
                        description="When enabled, each option navigates to a different page"
                        checked={(element as any).createBranches || false}
                        onChange={(e) => onChange({
                            createBranches: e.currentTarget.checked,
                            branches: e.currentTarget.checked ? { yes: undefined, no: undefined } : undefined
                        })}
                    />

                    {(element as any).createBranches && (
                        <Stack gap="xs" mt="sm">
                            <Select
                                label={`"${(element as any).yesLabel || 'Yes'}" goes to`}
                                placeholder="Select target page"
                                data={[
                                    ...(nodes || [])
                                        .filter((n) => n.type === 'page')
                                        .map((n) => ({ value: n.id, label: n.data.title || n.id })),
                                    { value: 'create-new-yes', label: '+ Create New Page' },
                                ]}
                                value={(element as any).branches?.yes ?? null}
                                error={branchTargetError((element as any).branches?.yes)}
                                onChange={(value) => {
                                    if (value === 'create-new-yes') {
                                        if (onAddNode) {
                                            const newPageId = onAddNode('Page');
                                            onChange({
                                                branches: {
                                                    ...(element as any).branches,
                                                    yes: newPageId
                                                }
                                            });
                                        }
                                    } else if (value) {
                                        onChange({
                                            branches: {
                                                ...(element as any).branches,
                                                yes: value
                                            }
                                        });
                                    }
                                }}
                            />
                            <Select
                                label={`"${(element as any).noLabel || 'No'}" goes to`}
                                placeholder="Select target page"
                                data={[
                                    ...(nodes || [])
                                        .filter((n) => n.type === 'page')
                                        .map((n) => ({ value: n.id, label: n.data.title || n.id })),
                                    { value: 'create-new-no', label: '+ Create New Page' },
                                ]}
                                value={(element as any).branches?.no ?? null}
                                error={branchTargetError((element as any).branches?.no)}
                                onChange={(value) => {
                                    if (value === 'create-new-no') {
                                        if (onAddNode) {
                                            const newPageId = onAddNode('Page');
                                            onChange({
                                                branches: {
                                                    ...(element as any).branches,
                                                    no: newPageId
                                                }
                                            });
                                        }
                                    } else if (value) {
                                        onChange({
                                            branches: {
                                                ...(element as any).branches,
                                                no: value
                                            }
                                        });
                                    }
                                }}
                            />
                        </Stack>
                    )}
                </>
            )}

            {element.type === 'MultiChoice' && (
                <>
                    <Select
                        label="Display Style"
                        value={(element as any).displayStyle || 'radio'}
                        onChange={(val) => onChange({ displayStyle: val as any })}
                        data={[
                            { value: 'radio', label: 'Radio Buttons' },
                            { value: 'buttons', label: 'Buttons' },
                            { value: 'checkbox', label: 'Checkboxes' },
                            { value: 'dropdown', label: 'Dropdown' },
                        ]}
                    />

                    <Text size="sm" fw={500} mt="xs">Options</Text>
                    <Stack gap="xs">
                        {((element as any).options || []).map((opt: string, idx: number) => (
                            <Group key={idx} gap="xs">
                                <TextInput
                                    style={{ flex: 1 }}
                                    aria-label={`Option ${idx + 1}`}
                                    value={opt}
                                    // A repeated label can't be told apart by the viewer or
                                    // paired to its own branch.
                                    error={
                                        ((element as any).options || []).indexOf(opt) !== idx
                                            ? 'Another option already has this label'
                                            : undefined
                                    }
                                    onChange={(e) => {
                                        // Branches are paired to options by label, so a rename has to
                                        // move the branch with it or the target is orphaned.
                                        onChange(withOptionRenamed(
                                            (element as any).options || [],
                                            (element as any).branches,
                                            idx,
                                            e.currentTarget.value,
                                        ));
                                    }}
                                />
                                <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    aria-label={`Remove option ${idx + 1}`}
                                    onClick={() => {
                                        // Drops the option's branch too — a branch with no option
                                        // still draws an edge or a dangling stub on the canvas.
                                        onChange(withOptionRemoved(
                                            (element as any).options || [],
                                            (element as any).branches,
                                            idx,
                                        ));
                                    }}
                                >
                                    <IconTrash size={14} />
                                </ActionIcon>
                            </Group>
                        ))}
                        <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => {
                                onChange(withOptionAdded(
                                    (element as any).options || [],
                                    (element as any).branches,
                                    nextOptionLabel((element as any).options || []),
                                ));
                            }}
                        >
                            Add Option
                        </Button>
                    </Stack>

                    <Divider label="Branching" labelPosition="center" my="sm" />

                    <Checkbox
                        label="Create branches to other pages"
                        description="When enabled, each option navigates to a different page"
                        checked={(element as any).createBranches || false}
                        onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            if (checked) {
                                // Initialize branches for each option
                                const initialBranches = ((element as any).options || []).map((opt: string) => ({
                                    option: opt,
                                    targetPageId: undefined
                                }));
                                onChange({
                                    createBranches: true,
                                    branches: initialBranches
                                });
                            } else {
                                onChange({
                                    createBranches: false,
                                    branches: undefined
                                });
                            }
                        }}
                    />

                    {(element as any).createBranches && (
                        <Stack gap="xs" mt="sm">
                            {((element as any).options || []).map((opt: string, idx: number) => {
                                const branch = ((element as any).branches || []).find((b: any) => b.option === opt);
                                return (
                                    <Select
                                        key={idx}
                                        label={`"${opt}" goes to`}
                                        placeholder="Select target page"
                                        data={[
                                            ...(nodes || [])
                                                .filter((n) => n.type === 'page')
                                                .map((n) => ({ value: n.id, label: n.data.title || n.id })),
                                            { value: `create-new-${idx}`, label: '+ Create New Page' },
                                        ]}
                                        value={branch?.targetPageId ?? null}
                                        error={branchTargetError(branch?.targetPageId)}
                                        onChange={(value) => {
                                            if (value?.startsWith('create-new')) {
                                                if (onAddNode) {
                                                    const newPageId = onAddNode('Page');
                                                    const updatedBranches = [...((element as any).branches || [])];
                                                    const branchIndex = updatedBranches.findIndex((b: any) => b.option === opt);
                                                    if (branchIndex >= 0) {
                                                        updatedBranches[branchIndex] = { option: opt, targetPageId: newPageId };
                                                    } else {
                                                        updatedBranches.push({ option: opt, targetPageId: newPageId });
                                                    }
                                                    onChange({ branches: updatedBranches });
                                                }
                                            } else if (value) {
                                                const updatedBranches = [...((element as any).branches || [])];
                                                const branchIndex = updatedBranches.findIndex((b: any) => b.option === opt);
                                                if (branchIndex >= 0) {
                                                    updatedBranches[branchIndex] = { option: opt, targetPageId: value };
                                                } else {
                                                    updatedBranches.push({ option: opt, targetPageId: value });
                                                }
                                                onChange({ branches: updatedBranches });
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    )}
                </>
            )}

            {element.type === 'Rating' && (
                <NumberInput
                    label="Max Rating"
                    value={(element as any).maxRating || 5}
                    onChange={(val) => onChange({ maxRating: Number(val) })}
                    min={1}
                    max={10}
                />
            )}

            {element.type === 'Button' && (
                <Stack gap="sm">
                    <TextInput
                        label="Button Text"
                        value={(element as any).buttonText || ''}
                        onChange={(e) => onChange({ buttonText: e.currentTarget.value })}
                    />
                    <Select
                        label="Style"
                        value={(element as any).buttonStyle || 'primary'}
                        onChange={(val) => onChange({ buttonStyle: val as any })}
                        data={[
                            { value: 'primary', label: 'Primary' },
                            { value: 'secondary', label: 'Secondary' },
                            { value: 'outline', label: 'Outline' },
                        ]}
                    />
                    <Select
                        label="Layout"
                        value={(element as any).buttonAlign || 'full'}
                        onChange={(val) => onChange({ buttonAlign: (val || 'full') as any })}
                        data={[
                            { value: 'full', label: 'Full width' },
                            { value: 'left', label: 'Fit text, left' },
                            { value: 'center', label: 'Fit text, center' },
                            { value: 'right', label: 'Fit text, right' },
                        ]}
                        allowDeselect={false}
                    />
                    <Select
                        label="Action"
                        value={(element as any).action || 'navigate'}
                        onChange={(val) => onChange({ action: val as any })}
                        data={[
                            { value: 'navigate', label: 'Navigate' },
                            { value: 'submit', label: 'Submit Form' },
                        ]}
                    />
                    {(element as any).action === 'navigate' && (
                        <Select
                            label="Target Page"
                            data={[
                                ...(nodes || [])
                                    .filter((n) => n.type === 'page')
                                    .map((n) => ({ value: n.id, label: n.data.title || n.id })),
                                { value: 'create-new', label: '+ Create New Page' },
                            ]}
                            value={(element as any).targetPageId ?? null}
                            onChange={(value) => {
                                if (value === 'create-new') {
                                    if (onAddNode) {
                                        const newPageId = onAddNode('Page');
                                        onChange(withTargetPageButtonText(newPageId));
                                    }
                                } else if (value) {
                                    onChange(withTargetPageButtonText(value));
                                }
                            }}
                        />
                    )}
                </Stack>
            )}

            {onRemove && (
                <>
                    <Divider my="sm" />
                    <Button
                        variant="outline"
                        color="red"
                        fullWidth
                        leftSection={<IconTrash size={16} />}
                        onClick={onRemove}
                    >
                        Remove Component
                    </Button>
                </>
            )}
        </Stack>
    );
}
